import { ReactElement } from 'react';
import { SupportedPlugins } from '..';
import { HeatMapTimeSeriesData } from './heatmap-data';
import { TabularData } from './tabular-data';
import {
  TooltipContextMenuCtx,
  SeriesMenuItemOptions,
  TimeSeriesData,
  TimeSeriesWithoutValues,
} from './time-series-data';
import { DistributionData } from './distribution-data';
import {
  ContextLinkDefinition,
  Notice,
  QueryStats,
  SingleValueData,
  SingleValueTimeSeriesData,
  TimeSeriesChartThresholdOptions,
} from '@/core';

/**
 * We use this key as part of the query key to identify our data queries
 */
export const DATA_QUERY_KEY = 'DataQuery';

/**
 * We use this key to identify the 'DataQuery' plugin. Used in getPlugin.
 */
export const DATA_QUERY_PLUGIN_KEY: keyof SupportedPlugins = 'DataQuery';

export type SupportedDataType = DataResponse['type'];
export type SupportedData = DataResponse['data'];

// Leveraging a Record here to ensure we keep this list up-to-date because typescript will require
// all keys of the union are listed. There unfortunately isn't built-in support for doing something
// similar with arrays. We then get the actual array we want below by using Object.keys on this
// record.
const SUPPORTED_DATA_TYPES_RECORD: Record<SupportedDataType, boolean> = {
  HeatMap: true,
  SingleValue: true,
  SingleValueTimeSeries: true,
  Table: true,
  TimeSeries: true,
  Distribution: true,
};
export const SUPPORTED_DATA_TYPES = Object.keys(SUPPORTED_DATA_TYPES_RECORD) as SupportedDataType[];

// Helper types for the data query plugin
interface DataResponseInterface<Type extends string, Data> {
  type: Type;
  data: Data;
}

export type TimeSeriesDataResponse = DataResponseInterface<'TimeSeries', TimeSeriesData>;

export type SingleValueDataResponse = DataResponseInterface<'SingleValue', SingleValueData>;

export type SingleValueTimeSeriesDataResponse = DataResponseInterface<
  'SingleValueTimeSeries',
  SingleValueTimeSeriesData
>;

export type HeatMapTimeSeriesDataResponse = DataResponseInterface<'HeatMap', HeatMapTimeSeriesData>;

export type TabularDataResponse = DataResponseInterface<'Table', TabularData>;

export type DistributionDataResponse = DataResponseInterface<'Distribution', DistributionData>;

export type DataResponse =
  | TimeSeriesDataResponse
  | SingleValueDataResponse
  | SingleValueTimeSeriesDataResponse
  | HeatMapTimeSeriesDataResponse
  | TabularDataResponse
  | DistributionDataResponse;

export interface QueryMetadata {
  notices?: Notice[];

  /**
   * The raw query that is executed to generate this data.
   * Useful when needing to inspect the query that was executed
   * after variables and other context modifications have been applied.
   */
  executedQueryString?: string;

  /**
   * The stats for the query execution.
   */
  stats?: QueryStats;

  /**
   * The thresholds that were applied to the query.
   */
  thresholds?: TimeSeriesChartThresholdOptions;

  /**
   * Links to other contexts that are relevant to this query.
   */
  links?: ContextLinkDefinition[];

  /**
   * Enables cursor-based pagination
   */
  pagination?: {
    /**
     * The cursor for the next page of results, if available.
     */
    cursor?: string;
    /**
     * Total number of rows.
     */
    totalRowCount?: number;
  };

  /**
   * Optional object for adding custom metadata
   */
  meta?: {
    [key: string]: unknown;
  };

  /**
   * Configuration for customizing the tooltip context menu
   */
  tooltipContextMenu?: (ctx: TooltipContextMenuCtx, series: TimeSeriesWithoutValues) => SeriesMenuItemOptions[];

  /**
   * Optional callback to render a custom legend icon for a series
   */
  renderSeriesIcon?: (series: TimeSeriesWithoutValues) => ReactElement;

  /**
   * Whether the query results have been truncated due to series limits.
   */
  truncated?: boolean;
}
